import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Row,
} from 'antd';
import { useMutation } from '@tanstack/react-query';
import { IDataModal } from '@/interfaces/dataModal';
import { IDoOrder } from '@/interfaces/doOrder';
import { addDoOrder, editDoOrder } from '@/adapters/doOrder';
import ProductSelector from '@/components/selector/product-selector';
import { IProduct } from '@/interfaces/product';
import locale from 'antd/es/date-picker/locale/th_TH';
import dayjs from 'dayjs';
import { dateFormat } from '@/configs/dateFormat';

interface IProps {
  dataModal?: IDataModal<Partial<IDoOrder>>;
  handleOk: () => void;
  handleCancel: () => void;
}

const DoOrderAddModal = ({ dataModal, handleOk, handleCancel }: IProps) => {
  const [form] = Form.useForm<IDoOrder>();
  const [search, setSearch] = useState<string>('');
  const mutations = useMutation({
    mutationFn: async (data: Partial<IDoOrder>) =>
      dataModal?.id ? await editDoOrder(data) : await addDoOrder(data),
    mutationKey: ['actionDoOrder'],
    onSuccess: data => {
      if (data.status !== 204) {
        throw new Error();
      } else {
        notification.success({
          message: 'เปิด DO ใหม่สำเร็จ',
        });
        handleOk();
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    form
      .validateFields()
      .then(async (values: Partial<IDoOrder>) => {
        values.id = Number(dataModal?.id || 0);
        values.requestEtdSin = dayjs(values.requestEtdSin).toDate();
        values.requestEtaBkk = dayjs(values.requestEtaBkk).toDate();
        values.updateEtdSin =
          values.updateEtdSin && dayjs(values.updateEtdSin).toDate();
        values.updateEtaBkk =
          values.updateEtaBkk && dayjs(values.updateEtaBkk).toDate();
        await mutations.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const onReset = () => {
    form.resetFields();
  };

  const onSelectProduct = (item: IProduct) => {
    form.setFieldsValue({
      productId: item?.id,
      productUnitName: item?.baseUnitName,
    });
  };

  const onPasteDo = (text: string) => {
    if (text) {
      // const format = text
      //   .split('\n')
      //   .map(s => s.replace(/["',\s]/g, ''))
      //   .filter(Boolean);
      // const dateStr = format[5];
      // const parsedDate = dayjs(dateStr, 'D/M/YYYY');
      // console.log(dateStr);
      // form.setFieldsValue({
      //   code: format[0]?.replace(/^0+/, ''),
      //   quantity: Number(format[4]?.match(/[\d.]+/)?.[0]),
      //   remain: Number(format[4]?.match(/[\d.]+/)?.[0]),
      //   requestEtdSin: parsedDate.isValid() ? parsedDate : undefined,
      // });
      const format = text.trim().split(/\s+/);
      const dateStr = format.at(-1); // last item is the date
      const parsedDate = dayjs(dateStr, 'D/M/YYYY');

      // Match numbers that contain a comma (e.g., 6,000.000 only)
      const quantityStr = format.find(
        s => /,/.test(s) && /^\d{1,3}(,\d{3})*(\.\d+)?$/.test(s),
      );
      const quantity = quantityStr
        ? Number(quantityStr.replace(/,/g, ''))
        : undefined;

      console.log('format:', format);
      console.log('quantityStr:', quantityStr);

      form.setFieldsValue({
        code: format[0]?.replace(/^0+/, ''), // '0000018035' → '18035'
        quantity, // from '6,000.000'
        remain: quantity, // same as quantity
        requestEtdSin: parsedDate.isValid() ? parsedDate : undefined,
      });

      setSearch(format[3]);
    }
  };

  useEffect(() => {
    if (dataModal?.id) {
      const item = dataModal?.item;
      form.setFieldsValue({
        ...item,
        requestEtdSin: dayjs(item?.requestEtdSin),
        createDate: dayjs(item?.createDate),
        requestEtaBkk: dayjs(item?.requestEtaBkk),
        updateEtdSin: item?.updateEtdSin && dayjs(item?.updateEtdSin),
        updateEtaBkk: item?.updateEtaBkk && dayjs(item?.updateEtaBkk),
      });
    }
  }, [dataModal?.id, dataModal?.item, form]);

  return (
    <Modal
      title={'Delivery Order'}
      open={dataModal?.open}
      onOk={() => handleUpdate()}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      width={600}
      footer={[
        <Button key="back" onClick={handleCancel}>
          ยกเลิก
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="doOrderGroup"
          loading={mutations.isPending}
          onClick={handleUpdate}
        >
          บันทึก
        </Button>,
      ]}
      afterClose={onReset}
    >
      <div className="flex w-full">
        <Form
          form={form}
          name="doOrder"
          onFinish={handleUpdate}
          layout="vertical"
          className="w-full"
          initialValues={{
            quantity: 1,
          }}
        >
          <Form.Item name={'productCode'} hidden />
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item<IDoOrder>
                label="DO NUMBER"
                name="code"
                rules={[{ required: true, message: 'ระบุ DO NUMBER' }]}
              >
                <Input size="large" placeholder="DO NUMBER" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<IDoOrder> label="INV NUMBER" name="ivNo">
                <Input size="large" placeholder="INV NUMBER" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item<IDoOrder>
                label="สินค้า"
                name={'productId'}
                rules={[{ required: true, message: 'ระบุ สินค้า' }]}
              >
                <ProductSelector
                  handleSelectData={onSelectProduct}
                  value={form.getFieldValue('productId')}
                  search={search}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<IDoOrder> label="จำนวน" name={'quantity'}>
                <InputNumber
                  disabled={!Form.useWatch('productId', form)}
                  size="large"
                  min={0}
                  className="w-full"
                  suffix={form.getFieldValue('productUnitName')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<IDoOrder> label="คงเหลือ" name={'remain'}>
                <InputNumber
                  // disabled={!dataModal?.id}
                  size="large"
                  min={0}
                  className="w-full"
                  suffix={form.getFieldValue('productUnitName')}
                />
              </Form.Item>
            </Col>
            <Col span={12} offset={12}>
              <Form.Item<Partial<IDoOrder>>
                label="CreateDate"
                name="createDate"
                rules={[{ required: true, message: 'เลือกวันที่' }]}
              >
                <DatePicker
                  size="large"
                  className="w-full"
                  locale={locale}
                  format={dateFormat}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<Partial<IDoOrder>>
                label="Request ETD SIN"
                name="requestEtdSin"
                rules={[{ required: true, message: 'เลือกวันที่' }]}
              >
                <DatePicker
                  size="large"
                  className="w-full"
                  locale={locale}
                  format={dateFormat}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<Partial<IDoOrder>>
                label="Request ETA BKK"
                name="requestEtaBkk"
                rules={[{ required: true, message: 'เลือกวันที่' }]}
              >
                <DatePicker
                  size="large"
                  className="w-full"
                  locale={locale}
                  format={dateFormat}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<Partial<IDoOrder>>
                label="Update ETD SIN"
                name="updateEtdSin"
              >
                <DatePicker
                  size="large"
                  className="w-full"
                  locale={locale}
                  format={dateFormat}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<Partial<IDoOrder>>
                label="Update ETA BKK"
                name="updateEtaBkk"
              >
                <DatePicker
                  size="large"
                  className="w-full"
                  locale={locale}
                  format={dateFormat}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="หมายเหตุ" name="remark">
                <Input.TextArea rows={1} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="INPUT DO FROM PDF" name="format">
                <Input.TextArea
                  rows={3}
                  placeholder="PASTE COPY HERE"
                  onChange={e => onPasteDo(e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default DoOrderAddModal;
